import React, {useCallback, useEffect, useState} from 'react';
import {connect} from 'react-redux';

import {
  getStructuredTableTogglableStatus,
  setStructuredTableTogglableStatus,
} from 'notebook/structured-tables/action-functions/toggle-edit-mode/StructuredTableTogglableStateActions';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import notifications from 'notifications';
import {BenchlingState} from 'redux-stores/reduceApp';
import usePrevious from 'ui-library/hooks/usePrevious';

import {StructuredTableEditStatus} from './reduceToggledTableEditState';

/**
 * Component for managing a structured table that has a togglable edit mode. It is tightly coupled
 * with the action functions which manage the buttons in the header (see makeToggleEditModeActionFunction)
 */

interface StructuredTableEditStatusManagerProps<TTableMetadata, TRowMetadata> {
  tableCid: string;
  table: StructuredTable<TTableMetadata, TRowMetadata>;
  children: (status: StructuredTableEditStatus) => React.ReactNode;
  submitHandler: () => Promise<void>;
  revertHandler: (revertTo: StructuredTable<TTableMetadata, TRowMetadata>) => void;
}

interface ConnectedProps {
  editStatus: StructuredTableEditStatus;
}

function mapStateToProps<TTableMetadata, TRowMetadata>(
  state: BenchlingState,
  props: StructuredTableEditStatusManagerProps<TTableMetadata, TRowMetadata>
): ConnectedProps {
  return {
    editStatus: getStructuredTableTogglableStatus(props.tableCid),
  };
}

/**
 * The logic lives here. This component does a few key things:
 *   - It listens for changes in the edit status, and fires callbacks when needed
 *   - It keeps a copy of the old table in case the user cancels the edit
 *
 * Exported for testing only.
 */
export function UnconnectedStructuredTableEditStatusManager<TTableMetadata, TRowMetadata>(
  props: StructuredTableEditStatusManagerProps<TTableMetadata, TRowMetadata> & ConnectedProps
): JSX.Element {
  const {submitHandler, revertHandler, table, tableCid, editStatus: newStatus} = props;
  const [cachedTable, setCachedTable] = useState(table);
  const oldStatus = usePrevious(props.editStatus);

  const submitCallback = useCallback(async () => {
    await submitHandler();
  }, [submitHandler]);
  const revertCallback = useCallback(() => {
    revertHandler(cachedTable);
  }, [revertHandler, cachedTable]);

  useEffect(() => {
    // We require many dependencies; ensure that we only update when really needed
    if (oldStatus === newStatus) {
      return;
    }

    if (newStatus === StructuredTableEditStatus.EDITING && oldStatus !== StructuredTableEditStatus.SAVING) {
      setCachedTable(table);
    }
    if (newStatus === StructuredTableEditStatus.SAVING) {
      (async () => {
        try {
          await submitCallback();
          setStructuredTableTogglableStatus(tableCid, StructuredTableEditStatus.LOCKED);
          // eslint-disable-next-line benchlint/always-throw-from-catch
        } catch (e) {
          setStructuredTableTogglableStatus(tableCid, StructuredTableEditStatus.EDITING);
          notifications.notifyError(e);
        }
      })();
    }
    if (newStatus === StructuredTableEditStatus.REVERTING) {
      revertCallback();
      setStructuredTableTogglableStatus(tableCid, StructuredTableEditStatus.LOCKED);
    }
  }, [oldStatus, newStatus, submitCallback, revertCallback, table, tableCid]);

  return <>{props.children(newStatus)}</>;
}

const StructuredTableEditStatusManager = connect(mapStateToProps)(
  UnconnectedStructuredTableEditStatusManager
);
StructuredTableEditStatusManager.displayName = 'StructuredTableEditStatusManager';

export default StructuredTableEditStatusManager;
